import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, useScroll } from "framer-motion";
import Lenis from "lenis";
import "lenis/dist/lenis.css";
import axios from "axios";
import { Nav } from "@/components/Nav";
import { Hero } from "@/components/Hero";
import { TrustBar } from "@/components/TrustBar";
import { Tools } from "@/components/Tools";
import { Formats } from "@/components/Formats";
import { Workflow } from "@/components/Workflow";
import { Outcomes } from "@/components/Outcomes";
import { BatchDemo } from "@/components/BatchDemo";
import { Proof } from "@/components/Proof";
import { Testimonials } from "@/components/Testimonials";
import { CtaBand } from "@/components/CtaBand";
import { Faq } from "@/components/Faq";
import { Pricing } from "@/components/Pricing";
import { Footer } from "@/components/Footer";
import { WingmanChat } from "@/components/WingmanChat";
import { LiveActivity } from "@/components/LiveActivity";
import { trackEvent } from "@/utils/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Landing() {
  const { scrollYProgress } = useScroll();
  const { hash } = useLocation();
  const homeViewTracked = useRef(false);
  const [activity, setActivity] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const hasTestimonials = testimonials.length > 0;

  useEffect(() => {
    axios.get(`${API}/activity`).then((r) => setActivity(r.data)).catch(() => {});
    axios.get(`${API}/testimonials`).then((r) => setTestimonials(r.data.testimonials || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    if (homeViewTracked.current) return;
    homeViewTracked.current = true;
    trackEvent("home_view", { source: "landing" });
    trackEvent("page_view", { source: "home" });
  }, []);

  useEffect(() => {
    if (!hash) return;
    const target = hash.slice(1);
    const timer = setTimeout(() => {
      document.getElementById(target)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
    return () => clearTimeout(timer);
  }, [hash]);

  return (
    <main className="bg-page text-ink" data-testid="landing-page">
      <motion.div
        data-testid="scroll-progress"
        className="fixed top-0 left-0 right-0 h-[2px] bg-azure origin-left z-[60] pointer-events-none"
        style={{ scaleX: scrollYProgress }}
      />
      <Nav />
      <Hero />
      <LiveActivity activity={activity} />
      <TrustBar />
      <Tools />
      <CtaBand />
      <Formats />
      <Workflow />
      <Outcomes />
      <BatchDemo />
      <Proof />
      <Testimonials items={testimonials} />
      <Faq indexLabel={hasTestimonials ? "08 — Common inquiries" : "07 — Common inquiries"} />
      <Pricing indexLabel={hasTestimonials ? "09 — Pricing" : "08 — Pricing"} />
      <Footer />
      <WingmanChat />
    </main>
  );
}
